import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PlatformMfaRenewal } from '@/components/legalControl/PlatformMfaRenewal'
import { Field, actionLabel } from '@/pages/vault/VaultForms'
import { digest, id, object, type Data } from '@/services/legalControl/workflow'
import {
  businessHistory,
  businessPreparationBody,
  inspectBusiness,
  type BusinessReference,
} from '@/services/legalControl/businessWorkflow'
import { useBusinessWorkflow } from '@/services/legalControl/useBusinessWorkflow'

function BusinessReviewDetails() {
  const { user, hasPermission } = useAuth()
  const allowed =
    !!user &&
    user.role === 'cml_admin' &&
    user.authScope === 'platform' &&
    !user.tenantId &&
    hasPermission('txh.platform.legal_control.subject.review') &&
    hasPermission('txh.platform.legal_control.subject.inspect')
  const flow = useBusinessWorkflow(allowed)
  const [request, setRequest] = useState(''),
    [source, setSource] = useState<Data | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({}),
    [evidence, setEvidence] = useState<Array<Record<string, string>>>([]),
    [attest, setAttest] = useState(false)
  const [review, setReview] = useState(''),
    [acceptance, setAcceptance] = useState(''),
    [intent, setIntent] = useState(''),
    [decisionId, setDecisionId] = useState(''),
    [reason, setReason] = useState(''),
    [noEffectReason, setNoEffectReason] = useState('operator_cancelled')
  const identity = `${user?.id}:${user?.role}:${user?.authScope}:${user?.tenantId}:${allowed}`
  const current = useRef(identity)
  current.current = identity
  const epoch = useRef(0),
    selection = useRef(request)
  selection.current = request
  useEffect(() => {
    epoch.current++
    setSource(null)
    setEvidence([])
    setAttest(false)
    setBusy(false)
    setError('')
    setReview('')
    setAcceptance('')
    setIntent('')
    setDecisionId('')
    setFields({})
    setReason('')
  }, [request, identity])
  useEffect(
    () => () => {
      epoch.current++
    },
    []
  )
  const [clock, setClock] = useState(Date.now())
  const deadline =
    typeof flow.view?.expires_at === 'string' ? Date.parse(flow.view.expires_at) : NaN
  useEffect(() => {
    if (!Number.isFinite(deadline) || deadline <= Date.now()) return
    const timer = setTimeout(
      () => setClock(Date.now()),
      Math.min(2147483647, deadline - Date.now() + 1)
    )
    return () => clearTimeout(timer)
  }, [deadline, clock])
  if (!allowed)
    return (
      <p role="alert">
        Use an independent platform reviewer with review and inspection permissions.
      </p>
    )
  const input = source ? object(source.command) : null
  async function load() {
    if (busy || flow.busy) return
    const owner = identity,
      selected = request,
      version = ++epoch.current
    setBusy(true)
    setSource(null)
    setEvidence([])
    setAttest(false)
    setError('')
    try {
      const result = await inspectBusiness(id(selected))
      if (owner !== current.current || selection.current !== selected || version !== epoch.current)
        return
      const command = object(result.command)
      setEvidence(
        (command.declared_evidence as unknown[]).map(v => {
          const e = object(v)
          return {
            evidence_id: id(e.evidence_id),
            digest: String(e.digest),
            category: '',
            custody_reference: '',
            inspected_bytes_sha256: '',
          }
        })
      )
      setSource(result)
    } catch {
      if (owner === current.current && version === epoch.current)
        setError(
          'Current business request unavailable. Check the reference,permissions and fresh MFA.'
        )
    } finally {
      if (owner === current.current && version === epoch.current) setBusy(false)
    }
  }
  function record() {
    try {
      setError('')
      const duration = Number(fields.seconds),
        end = new Date(fields.expires ?? '')
      if (
        !source ||
        !attest ||
        !['approve', 'reject'].includes(fields.decision) ||
        ![fields.policyHash, fields.policyEvidence, fields.recordHash].every(v =>
          digest.test(v ?? '')
        ) ||
        !/^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(fields.rule ?? '') ||
        !Number.isInteger(duration) ||
        duration < 1 ||
        duration > 2147483647 ||
        !Number.isFinite(end.getTime()) ||
        end.getTime() <= Date.now() ||
        evidence.some(
          e =>
            !/^[A-Za-z][A-Za-z0-9._-]{0,79}$/.test(e.category) ||
            e.custody_reference.trim() !== e.custody_reference ||
            e.custody_reference.length < 8 ||
            e.custody_reference.length > 256 ||
            e.inspected_bytes_sha256 !== e.digest
        )
      )
        throw new Error()
      const command = crypto.randomUUID()
      setReview(command)
      void flow.run(
        { kind: 'review', target: id(request), command, status: 'pending' },
        {
          review_id: command,
          expected_ordinal: 1,
          policy_id: id(fields.policy),
          policy_body_sha256: fields.policyHash,
          rule_id: fields.rule,
          authority_seconds: duration,
          policy_evidence_sha256: fields.policyEvidence,
          reviewed_evidence: evidence.map(e => ({ ...e })),
          decision: fields.decision,
          decision_record_sha256: fields.recordHash,
          expires_at: end.toISOString(),
          attest_policy_document_and_evidence_reviewed: true,
          acknowledge_no_controller_authority: true,
        }
      )
    } catch {
      setError(
        'Complete the approved policy,evidence inspection,decision and future review validity before recording.'
      )
    }
  }
  async function prepare() {
    try {
      setError('')
      const command = crypto.randomUUID(),
        decision = crypto.randomUUID()
      const ref: BusinessReference = {
        kind: 'prepare',
        target: id(request),
        command,
        status: 'pending',
        preparation: { review: id(review), acceptance: id(acceptance), decision },
      }
      setIntent(command)
      setDecisionId(decision)
      await flow.run(ref, businessPreparationBody(ref))
    } catch {
      setError(
        'Use the original intake,recorded review and independent account acceptance references.'
      )
    }
  }
  function step(kind: 'issue' | 'cancel' | 'reconcile' | 'project' | 'revoke') {
    try {
      setError('')
      const target = id(intent)
      const known = businessHistory(user!.id).find(r => r.kind === kind && r.target === target)
      if (known) {
        void flow.run(known, kind === 'reconcile' ? {} : undefined)
        return
      }
      const command = ['issue', 'cancel', 'reconcile'].includes(kind)
        ? id(decisionId)
        : crypto.randomUUID()
      if (
        kind === 'cancel' &&
        !['operator_cancelled', 'not_eligible', 'withdrawn'].includes(noEffectReason)
      )
        throw new Error('Choose a supported no-effect reason')
      const ref: BusinessReference = { kind, target, command, status: 'pending' }
      if (kind === 'cancel') ref.reason = noEffectReason as BusinessReference['reason']
      const body: Data =
        kind === 'reconcile'
          ? {}
          : kind === 'issue' || kind === 'cancel'
            ? {
                intent_id: target,
                decision_id: command,
                action: kind === 'issue' ? 'issued' : 'no_effect',
                reason: kind === 'issue' ? null : noEffectReason,
              }
            : { intent_id: target, command_id: command }
      if (kind === 'revoke') {
        if (reason.trim().length < 8 || reason.trim().length > 512) throw new Error()
        body.reason = reason.trim()
      }
      void flow.run(ref, body)
    } catch {
      setError(
        'Use the original intent/decision references and a specific revocation reason of8–512 characters.'
      )
    }
  }
  const expired =
    flow.view?.state === 'active' &&
    typeof flow.view.expires_at === 'string' &&
    Date.parse(flow.view.expires_at) <= Math.max(clock, Date.now())
  const selectedIntent =
    flow.view?.command &&
    object(flow.view.command).intent_id === intent &&
    flow.view.decision_id === decisionId
  const canDecide = !!selectedIntent && flow.view?.state === 'reserved'
  const canProject =
    !!selectedIntent &&
    ['issued_pending_projection', 'issued_projected', 'issued_lapsed'].includes(
      String(flow.view?.state)
    )
  const appointment =
    flow.view?.authority === 'governed_business_controller_projection' &&
    flow.view.intent_id === intent
      ? flow.view
      : null
  const canRevoke =
    flow.view?.intent_id === intent && ['active', 'expired'].includes(String(flow.view?.state))
  return (
    <main className="space-y-6 text-text-primary">
      <h1 className="text-2xl font-semibold">Business appointment reviews</h1>
      <p>
        Inspect the firm request under an approved evidence policy. The proposed account must accept
        the exact reviewed terms. Human review and admission receipts alone do not grant an active
        appointment.
      </p>
      <fieldset className="card p-5 space-y-3" disabled={busy || flow.busy}>
        <legend className="font-semibold">Selected business request</legend>
        <Field
          label="Business intake reference"
          value={request}
          onChange={e => setRequest(e.target.value)}
        />
        <button className="btn" onClick={() => void load()}>
          Inspect business request
        </button>
        {input && (
          <div>
            <p>Business subject: {String(object(source!.subject).subject_id)}</p>
            <p>Proposed account: {String(input.proposed_account_id)}</p>
            <p>
              Requested role:{' '}
              {input.requested_role === 'representative' ? 'Representative' : 'Legal controller'}
            </p>
            <p>Capacity: {String(input.declared_capacity)}</p>
            <ul>
              {(input.requested_actions as string[]).map(a => (
                <li key={a}>{actionLabel(a)}</li>
              ))}
            </ul>
            <p>The server checks current eligibility again before recording and admission.</p>
          </div>
        )}
      </fieldset>
      <PlatformMfaRenewal />
      <fieldset className="card p-5 space-y-3" disabled={!source || flow.locked || busy}>
        <legend className="font-semibold">Human business evidence review</legend>
        {(
          [
            ['policy', 'Approved policy reference'],
            ['policyHash', 'Approved policy fingerprint'],
            ['rule', 'Approved policy rule'],
            ['seconds', 'Appointment duration (seconds)'],
            ['policyEvidence', 'Policy evidence SHA-256'],
            ['recordHash', 'Sealed decision record SHA-256'],
          ] as const
        ).map(([key, label]) => (
          <Field
            key={key}
            label={label}
            value={fields[key] ?? ''}
            onChange={e => {
              setFields(f => ({ ...f, [key]: e.target.value }))
              setAttest(false)
            }}
          />
        ))}
        {evidence.map((e, i) => (
          <div key={e.evidence_id} className="border rounded p-3">
            <p>
              Evidence {i + 1}: {e.evidence_id}
            </p>
            <p>Declared fingerprint: {e.digest}</p>
            {(
              [
                ['category', 'Evidence category'],
                ['custody_reference', 'Sealed custody reference'],
                ['inspected_bytes_sha256', 'Inspected bytes SHA-256'],
              ] as const
            ).map(([key, label]) => (
              <Field
                key={key}
                label={`${label} ${i + 1}`}
                value={e[key]}
                onChange={event => {
                  setAttest(false)
                  setEvidence(rows =>
                    rows.map((v, n) => (n === i ? { ...v, [key]: event.target.value } : v))
                  )
                }}
              />
            ))}
          </div>
        ))}
        <label className="block">
          Review decision
          <select
            className="input w-full"
            value={fields.decision ?? ''}
            onChange={e => {
              setFields(f => ({ ...f, decision: e.target.value }))
              setAttest(false)
            }}
          >
            <option value="">Choose a decision</option>
            <option value="approve">Approve reviewed request</option>
            <option value="reject">Reject reviewed request</option>
          </select>
        </label>
        <Field
          label="Review valid until"
          type="datetime-local"
          value={fields.expires ?? ''}
          onChange={e => {
            setFields(f => ({ ...f, expires: e.target.value }))
            setAttest(false)
          }}
        />
        <label>
          <input type="checkbox" checked={attest} onChange={e => setAttest(e.target.checked)} />I
          inspected the policy document and all declared evidence and recorded my independent
          decision.
        </label>
        <button className="btn btn-primary" disabled={!attest} onClick={record}>
          Record business review
        </button>
      </fieldset>
      <fieldset className="card p-5 space-y-3" disabled={flow.locked || busy}>
        <legend className="font-semibold">Prepare the accepted request</legend>
        <Field
          label="Recorded business review reference"
          value={review}
          onChange={e => setReview(e.target.value)}
        />
        <Field
          label="Account acceptance reference"
          value={acceptance}
          onChange={e => setAcceptance(e.target.value)}
        />
        <button className="btn" onClick={() => void prepare()}>
          Prepare accepted business request
        </button>
      </fieldset>
      <fieldset className="card p-5 space-y-3" disabled={flow.busy || busy || flow.broken}>
        <legend className="font-semibold">Original admission and appointment</legend>
        <Field
          label="Business intent reference"
          value={intent}
          onChange={e => setIntent(e.target.value)}
        />
        <Field
          label="Original business decision reference"
          value={decisionId}
          onChange={e => setDecisionId(e.target.value)}
        />
        <button
          className="btn"
          onClick={async () => {
            const result = await flow.readIntent(intent)
            if (result && current.current === identity)
              setDecisionId(result.ref.preparation!.decision)
          }}
        >
          Inspect original business intent
        </button>
        <p>
          Keep these original references across interruptions. Reconciliation checks the original
          decision;it does not create a replacement.
        </p>
        <label className="block">
          Business no-effect reason
          <select
            className="input"
            value={noEffectReason}
            disabled={flow.locked}
            onChange={e => setNoEffectReason(e.target.value)}
          >
            <option value="operator_cancelled">Reviewer cancelled the request</option>
            <option value="not_eligible">The request is not eligible</option>
            <option value="withdrawn">The account withdrew consent</option>
          </select>
        </label>
        <div className="flex flex-wrap gap-3">
          <button
            className="btn"
            disabled={flow.locked || !canDecide}
            onClick={() => step('issue')}
          >
            Issue business appointment decision
          </button>
          <button
            className="btn"
            disabled={flow.locked || !canDecide}
            onClick={() => step('cancel')}
          >
            Record original no-effect decision
          </button>
          <button className="btn" onClick={() => step('reconcile')}>
            Reconcile original business decision
          </button>
          <button
            className="btn"
            disabled={flow.locked || !canProject}
            onClick={() => step('project')}
          >
            Activate business appointment
          </button>
        </div>
        <button className="btn" onClick={() => void flow.readController(intent)}>
          Inspect existing business appointment
        </button>
        <Field
          label="Business revocation reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <button className="btn" disabled={flow.locked || !canRevoke} onClick={() => step('revoke')}>
          Revoke business appointment
        </button>
      </fieldset>
      {flow.view?.state === 'expired' && flow.view?.intent_id === intent && (
        <p role="note">
          This appointment has expired and no longer grants access. Record a reason and revoke the
          expired appointment before starting a fresh review and account consent for renewal.
        </p>
      )}
      {appointment && (
        <section aria-label="Inspected business appointment" className="card p-5 space-y-3">
          <h2>Inspected appointment</h2>
          <p>
            This record belongs to the account shown below. Check it before revoking; it may differ
            from the selected request above. To replace this person, revoke this appointment and
            submit a fresh request for the replacement account, with independent review and consent.
          </p>
          <dl className="space-y-2">
            {[
              ['Account', 'account_id'],
              ['Client', 'client_id'],
              ['Appointment', 'controller_id'],
              ['Intent', 'intent_id'],
              ['Role', 'role'],
              ['State', 'state'],
              ['Expires at', 'expires_at'],
            ].map(([label, key]) => (
              <div key={key}>
                <dt className="font-medium">{label}</dt>
                <dd className="break-all">{String(appointment[key] ?? 'Not applied')}</dd>
              </div>
            ))}
            <div>
              <dt className="font-medium">Actions</dt>
              <dd>
                {Array.isArray(appointment.actions)
                  ? appointment.actions.map(String).join(', ')
                  : 'Unavailable'}
              </dd>
            </div>
          </dl>
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      <section aria-label="Original business command history" className="card p-5 space-y-3">
        {flow.error && <p role="alert">{flow.error}</p>}
        {flow.view && (
          <p role="status">
            Recorded business outcome:{' '}
            {String(expired ? 'expired' : (flow.view.state ?? flow.view.decision ?? 'confirmed'))}.{' '}
            {flow.view.authority_granted === false
              ? 'This record does not itself grant data access.'
              : ''}
          </p>
        )}
        {flow.references.map(r => (
          <div key={r.kind + ':' + r.command}>
            <p>
              {r.kind}: {r.status === 'pending' ? 'Outcome unconfirmed' : 'Recorded'}
            </p>
            <p className="break-all">Original reference: {r.command}</p>
            <button
              className="btn"
              disabled={flow.busy}
              onClick={() => {
                if (r.kind === 'prepare') {
                  setIntent(r.command)
                  setDecisionId(r.preparation!.decision)
                } else if (r.kind !== 'review') {
                  setIntent(r.target)
                  if (['issue', 'cancel', 'reconcile'].includes(r.kind)) setDecisionId(r.command)
                }
                void flow.run(r)
              }}
            >
              Check original business outcome
            </button>
            {r.kind === 'cancel' && r.status === 'pending' && (
              <button
                className="btn"
                disabled={flow.busy || flow.broken}
                onClick={() => void flow.retryCancellation(r)}
              >
                Retry original business no-effect decision
              </button>
            )}
            {r.kind === 'prepare' && r.status === 'pending' && (
              <button
                className="btn"
                disabled={flow.busy || flow.broken}
                onClick={() => void flow.retryPreparation(r)}
              >
                Retry original business preparation
              </button>
            )}
          </div>
        ))}
      </section>
    </main>
  )
}
export function BusinessLegalSubjectReviewPage() {
  const { user } = useAuth()
  return (
    <BusinessReviewDetails key={`${user?.id}:${user?.authScope}:${user?.role}:${user?.tenantId}`} />
  )
}
